// Reusable folder-tree rail — iterative pre-order DFS render (no recursive JSX),
// expand/collapse openMap, auto-expand ancestors of the selected folder, direct-child
// doc counts, hidden-folder pill (internal), and native HTML5 drag-drop targets.
import { useEffect, useMemo, useState } from "react";
import { ChevronRight, ChevronDown, Folder as FolderIcon, FolderOpen, Eye, EyeOff, Inbox, Layers } from "lucide-react";

const ROOT = "__root__";

export default function FolderTree({
    folders = [], docCounts = {}, totalCount = 0, unfiledCount = 0,
    selectedId, onSelect, onDropDoc, onReparent, canEdit, isInternal, hidePseudo = false,
}) {
    const [openMap, setOpenMap] = useState({});
    const [dragOverId, setDragOverId] = useState(null);

    const byId = useMemo(() => Object.fromEntries(folders.map((f) => [f.id, f])), [folders]);

    const childrenByParent = useMemo(() => {
        const m = {};
        folders.forEach((f) => { (m[f.parent_id || ROOT] ||= []).push(f); });
        Object.values(m).forEach((arr) =>
            arr.sort((a, b) => (a.sort_order - b.sort_order) || a.name.localeCompare(b.name)));
        return m;
    }, [folders]);

    // Auto-expand ancestors of the selected folder
    useEffect(() => {
        if (!selectedId || !byId[selectedId]) return;
        const opens = {};
        let cur = byId[selectedId];
        let guard = 0;
        while (cur && cur.parent_id && guard < 8) { opens[cur.parent_id] = true; cur = byId[cur.parent_id]; guard++; }
        setOpenMap((m) => ({ ...m, ...opens }));
    }, [selectedId, byId]);

    const flat = useMemo(() => {
        const out = [];
        const walk = (parentId, depth) => {
            (childrenByParent[parentId] || []).forEach((f) => {
                out.push({ f, depth, hasChildren: !!(childrenByParent[f.id] || []).length });
                if (openMap[f.id]) walk(f.id, depth + 1);
            });
        };
        walk(ROOT, 0);
        return out;
    }, [childrenByParent, openMap]);

    const toggle = (id) => setOpenMap((m) => ({ ...m, [id]: !m[id] }));

    const handleDrop = (e, targetId) => {
        e.preventDefault();
        setDragOverId(null);
        const docsRaw = e.dataTransfer.getData("pr/docs");
        const docId = e.dataTransfer.getData("pr/doc");
        const folderId = e.dataTransfer.getData("pr/folder");
        if (docsRaw && onDropDoc) {
            try {
                const ids = JSON.parse(docsRaw);
                if (ids.length) onDropDoc(targetId, ids);
            } catch { /* ignore malformed payload */ }
        } else if (docId && onDropDoc) onDropDoc(targetId, [docId]);
        else if (folderId && folderId !== targetId && onReparent) onReparent(folderId, targetId);
    };

    const PseudoRow = ({ id, icon: Icon, label, count }) => (
        <button data-testid={`folder-node-${id}`} onClick={() => onSelect(id)}
            onDragOver={canEdit ? (e) => { e.preventDefault(); setDragOverId(id); } : undefined}
            onDragLeave={() => setDragOverId(null)}
            onDrop={canEdit && id === "__unfiled__" ? (e) => handleDrop(e, null) : undefined}
            className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[13px] text-left transition-colors
                ${selectedId === id ? "bg-[#FBEFE5] text-[#9F4212] font-semibold" : "text-[#3e4b63] hover:bg-[#F6F4F0]"}
                ${dragOverId === id ? "ring-2 ring-[var(--brand-accent,#C75A1B)] ring-inset" : ""}`}>
            <Icon size={15} className="shrink-0 opacity-80" />
            <span className="flex-1 truncate">{label}</span>
            {count > 0 && <span className="text-[11px] text-[#9A968F] font-mono">{count}</span>}
        </button>
    );

    return (
        <div className="space-y-0.5" data-testid="folder-tree">
            {!hidePseudo && <PseudoRow id="__all__" icon={Layers} label="All documents" count={totalCount} />}
            {!hidePseudo && <PseudoRow id="__unfiled__" icon={Inbox} label="Unfiled" count={unfiledCount} />}
            {!hidePseudo && <div className="h-px bg-[#F0EDE8] my-1.5" />}
            {flat.length === 0 && (
                <div className="px-2.5 py-3 text-[12px] italic text-[#9A968F]" data-testid="folder-tree-empty">No folders yet.</div>
            )}
            {flat.map(({ f, depth, hasChildren }) => (
                <div key={f.id}
                    draggable={canEdit && isInternal}
                    onDragStart={(e) => e.dataTransfer.setData("pr/folder", f.id)}
                    onDragOver={canEdit ? (e) => { e.preventDefault(); setDragOverId(f.id); } : undefined}
                    onDragLeave={() => setDragOverId(null)}
                    onDrop={canEdit ? (e) => handleDrop(e, f.id) : undefined}>
                    <div data-testid={`folder-node-${f.id}`}
                        onClick={() => onSelect(f.id)}
                        style={{ paddingLeft: `${depth * 16 + 6}px` }}
                        className={`group/folder flex items-center gap-1.5 pr-2 py-1.5 rounded-lg text-[13px] cursor-pointer transition-colors
                            ${selectedId === f.id ? "bg-[#FBEFE5] text-[#9F4212] font-semibold" : "text-[#3e4b63] hover:bg-[#F6F4F0]"}
                            ${dragOverId === f.id ? "ring-2 ring-[var(--brand-accent,#C75A1B)] ring-inset" : ""}`}>
                        {hasChildren ? (
                            <button data-testid={`folder-toggle-${f.id}`} onClick={(e) => { e.stopPropagation(); toggle(f.id); }}
                                className="p-0.5 rounded hover:bg-[#EDE8E1] shrink-0">
                                {openMap[f.id] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </button>
                        ) : <span className="w-[19px] shrink-0" />}
                        {openMap[f.id] && hasChildren
                            ? <FolderOpen size={15} className="shrink-0" style={{ color: f.color || "#C68A1C" }} />
                            : <FolderIcon size={15} className="shrink-0" style={{ color: f.color || "#C68A1C" }} />}
                        <span className="flex-1 truncate">{f.name}</span>
                        {isInternal && (f.portal_visible ? (
                            <span data-testid={`folder-visible-${f.id}`} title="Visible to client portal"
                                className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full" style={{ background: "#E4F1EC", color: "#2E6B57" }}>
                                <Eye size={9} /> Visible
                            </span>
                        ) : (
                            <span data-testid={`folder-hidden-${f.id}`} title="Hidden from client portal"
                                className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-[#EDE8E1] text-[#8a8378]">
                                <EyeOff size={9} /> Hidden
                            </span>
                        ))}
                        {docCounts[f.id] > 0 && <span className="text-[11px] text-[#9A968F] font-mono">{docCounts[f.id]}</span>}
                    </div>
                </div>
            ))}
        </div>
    );
}
